import { useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { Button, FilePicker, toast } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { downloadBlob } from '../../../../../lib/downloadBlob';
import { useIntegrationMutations } from '../../hooks';
import { IntegrationMappingType, type Integration } from '../../model';

export function IntegrationMappings({ integration }: { integration?: Integration | undefined }) {
  return (
    <section className="border-input overflow-hidden rounded-md border">
      <header className="bg-muted/40 border-input border-b px-4 py-3">
        <h2 className="text-sm font-semibold">{dashboardT('integrations.mappings')}</h2>
        <p className="text-muted-foreground mt-0.5 text-xs">
          {dashboardT('integrations.mappingsDescription')}
        </p>
      </header>
      <div className="divide-border divide-y">
        <IntegrationMappingRow type={IntegrationMappingType.Bundle} integration={integration} />
        <IntegrationMappingRow type={IntegrationMappingType.Domain} integration={integration} />
      </div>
    </section>
  );
}

function IntegrationMappingRow({
  type,
  integration,
}: {
  type: IntegrationMappingType;
  integration?: Integration | undefined;
}) {
  const mutations = useIntegrationMutations();
  const [file, setFile] = useState<File | null>(null);
  const bundle = type === IntegrationMappingType.Bundle;
  const count = bundle ? integration?.bundleMappingsCount : integration?.domainMappingsCount;
  const title = dashboardT(bundle ? 'integrations.bundleMappings' : 'integrations.domainMappings');
  const busy = mutations.downloadMappingTemplate.isPending || mutations.uploadMappings.isPending;

  async function downloadTemplate() {
    try {
      const blob = await mutations.downloadMappingTemplate.mutateAsync(type);
      downloadBlob(blob, bundle ? 'bundle-mappings-template.csv' : 'domain-mappings-template.csv');
    } catch (error) {
      showRequestError(error, dashboardT('integrations.mappingDownloadError'));
    }
  }

  async function upload() {
    if (!file || !integration) return;
    try {
      await mutations.uploadMappings.mutateAsync({ integrationId: integration.id, type, file });
      setFile(null);
      toast.success(dashboardT('integrations.mappingUploadSuccess'));
    } catch (error) {
      showRequestError(error, dashboardT('integrations.mappingUploadError'));
    }
  }

  return (
    <div className="grid gap-4 px-4 py-4 lg:grid-cols-[170px_minmax(0,1fr)_auto] lg:items-end">
      <div className="self-center">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-muted-foreground mt-0.5 text-xs">
          {dashboardT('integrations.mappingCount', { count: count ?? 0 })}
        </p>
      </div>
      <FilePicker
        className="min-w-0"
        label={dashboardT('integrations.mappingFile')}
        buttonLabel={dashboardT('integrations.chooseMappingFile')}
        buttonClassName="shrink-0"
        accept=".csv,text/csv"
        value={file}
        onFileChange={setFile}
        disabled={busy || !integration}
        helperText={
          integration
            ? dashboardT('integrations.mappingFileHelp')
            : dashboardT('integrations.saveBeforeMappings')
        }
        helperTextClassName="min-w-0 flex-1 truncate text-xs"
      />
      <div className="flex flex-wrap gap-2 lg:justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => void downloadTemplate()}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          {dashboardT('integrations.downloadTemplate')}
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!file || !integration || busy}
          onClick={() => void upload()}
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {dashboardT('integrations.upload')}
        </Button>
      </div>
    </div>
  );
}
